/**
 * M-Doc property gate — loaded from backend /mdoc/clientConfig.
 * Server source of truth: JV-Backend .env MDOC_PROPERTY_ID, MDOC_PROPERTY_NAME.
 */
import { apiCall } from "./httpClient";
import apiEndPoints from "./apiEndPoints";

const normalize = (value) => String(value || "").trim().toLowerCase();

let serverConfig = null;
let hydratePromise = null;
const configListeners = new Set();

function notifyConfigListeners() {
  configListeners.forEach((listener) => {
    try {
      listener(serverConfig);
    } catch (error) {
      console.warn("mdoc config listener failed:", error?.message || error);
    }
  });
}

export function isMdocClientConfigLoaded() {
  return serverConfig !== null;
}

/** Subscribe to M-Doc config hydration; returns unsubscribe. */
export function subscribeMdocClientConfig(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }
  configListeners.add(listener);
  if (serverConfig) {
    listener(serverConfig);
  }
  return () => configListeners.delete(listener);
}

export function setMdocClientConfig(config) {
  if (!config || typeof config !== "object") {
    return;
  }
  serverConfig = {
    emailRequired:
      config.emailRequired === undefined ? true : Boolean(config.emailRequired),
    propertyIds: Array.isArray(config.propertyIds) ? config.propertyIds : [],
    propertyNames: Array.isArray(config.propertyNames)
      ? config.propertyNames.map((name) => normalize(name))
      : [],
  };
  notifyConfigListeners();
}

export function fetchMdocClientConfigFromApi() {
  return apiCall("post", apiEndPoints.MDOC_CLIENT_CONFIG, {});
}

export function isMdocProperty(propertyId, propertyTitle) {
  if (!serverConfig) {
    return false;
  }

  const id = String(propertyId || "").trim();
  const title = normalize(propertyTitle);
  const { propertyIds, propertyNames } = serverConfig;

  if (id && propertyIds.includes(id)) {
    return true;
  }

  if (title && propertyNames.includes(title)) {
    return true;
  }

  return false;
}

export function isMdocEmailRequired(propertyId, propertyTitle) {
  if (!serverConfig) {
    return false;
  }
  if (serverConfig.emailRequired === false) {
    return false;
  }
  return isMdocProperty(propertyId, propertyTitle);
}

/** Load M-Doc property list from backend env (deduped in-flight). */
export async function ensureMdocClientConfigHydrated(
  fetchConfig = fetchMdocClientConfigFromApi,
) {
  if (serverConfig) {
    return serverConfig;
  }

  if (!hydratePromise) {
    hydratePromise = (async () => {
      if (typeof fetchConfig !== "function") {
        return null;
      }

      try {
        const response = await fetchConfig();
        const payload = response?.data?.data ?? response?.data ?? response;
        if (payload) {
          setMdocClientConfig(payload);
        }
      } catch (error) {
        console.warn(
          "ensureMdocClientConfigHydrated failed:",
          error?.message || error,
        );
      } finally {
        hydratePromise = null;
      }

      return serverConfig;
    })();
  }

  return hydratePromise;
}

/** @deprecated Use ensureMdocClientConfigHydrated */
export async function hydrateMdocClientConfig(fetchConfig) {
  return ensureMdocClientConfigHydrated(fetchConfig);
}
